import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageHeader, Panel, Loading, Pill } from "@/components/kit";
import OrgDateFilter from "@/components/OrgDateFilter";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";

const EMPTY = { from_date: "", to_date: "", opco_id: "", region_id: "", rso_id: "", ao_id: "", dealer_id: "", status: "", severity: "" };

export default function Exceptions() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [filters, setFilters] = useState(EMPTY);
  const [sel, setSel] = useState(null);
  const [resolution, setResolution] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["exceptions", filters],
    queryFn: async () => (await api.get("/exceptions", { params: Object.fromEntries(Object.entries(filters).filter(([, x]) => x)) })).data,
    placeholderData: (p) => p,
  });

  const resolve = useMutation({
    mutationFn: async ({ id, body }) => (await api.patch(`/exceptions/${id}`, body)).data,
    onSuccess: () => { toast.success("Exception updated"); setSel(null); setResolution(""); qc.invalidateQueries({ queryKey: ["exceptions"] }); },
    onError: (e) => toast.error(e.response?.data?.detail || "Update failed"),
  });

  if (isLoading || !data) return <Loading label="Loading exception workbench…" />;

  return (
    <div>
      <PageHeader title="Exception Workbench" subtitle="Auto-detected validation & calculation exceptions with resolution audit trail" />
      <OrgDateFilter value={filters} onChange={setFilters}>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-1">Severity</div>
          <select className="h-9 rounded-md border border-slate-200 px-2.5 bg-white text-sm w-full" data-testid="ex-filter-severity"
            value={filters.severity} onChange={(e) => setFilters((f) => ({ ...f, severity: e.target.value }))}>
            <option value="">All</option><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
          </select>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-1">Status</div>
          <select className="h-9 rounded-md border border-slate-200 px-2.5 bg-white text-sm w-full" data-testid="ex-filter"
            value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}>
            <option value="">All</option><option value="open">Open</option><option value="investigating">Investigating</option><option value="resolved">Resolved</option>
          </select>
        </div>
      </OrgDateFilter>

      <Panel testid="ex-table-panel" subtitle={`${data.exceptions.length} exceptions`}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-400 text-xs uppercase tracking-wider border-b border-slate-100">
                <th className="py-2.5 pr-4">Type</th><th className="py-2.5 pr-4">Dealer</th>
                <th className="py-2.5 pr-4">Severity</th><th className="py-2.5 pr-4">Status</th>
                <th className="py-2.5 pr-4">Message</th><th className="py-2.5 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {data.exceptions.map((e, i) => (
                <tr key={e.id} className="drill-row border-b border-slate-50" data-testid={`ex-row-${i}`}>
                  <td className="py-3 pr-4 font-medium text-slate-800">{e.type}</td>
                  <td className="py-3 pr-4 font-mono text-xs text-slate-500">{e.dealer_code}</td>
                  <td className="py-3 pr-4"><Pill tone={e.severity}>{e.severity}</Pill></td>
                  <td className="py-3 pr-4"><Pill tone={e.status}>{e.status}</Pill></td>
                  <td className="py-3 pr-4 text-slate-500 max-w-xs truncate">{e.message}</td>
                  <td className="py-3 text-right">
                    {e.status !== "resolved" && (
                      <Button size="sm" variant="outline" data-testid={`ex-resolve-${i}`} onClick={() => setSel(e)}>Resolve</Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="bg-white" data-testid="ex-resolve-modal">
          <DialogHeader><DialogTitle className="font-heading">Resolve Exception</DialogTitle></DialogHeader>
          {sel && (
            <div className="space-y-4">
              <div className="text-sm text-slate-600"><b>{sel.type}</b> · dealer {sel.dealer_code}</div>
              <Textarea placeholder="Resolution notes / dispute override reason…" rows={3}
                data-testid="ex-resolution-input" value={resolution} onChange={(e) => setResolution(e.target.value)} />
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => resolve.mutate({ id: sel.id, body: { resolution, status: "investigating" } })}
                  data-testid="ex-investigate-btn">Mark Investigating</Button>
                <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="ex-confirm-resolve-btn"
                  disabled={!resolution} onClick={() => resolve.mutate({ id: sel.id, body: { resolution, status: "resolved" } })}>
                  Resolve
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
